import React, { useCallback, useMemo, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { FiltersBar } from "./FiltersBar";
import { DataTable } from "./DataTable";
import { ReportToolbar } from "./ReportToolbar";
import { ProficiencyBucketChart } from "./ProficiencyBucketChart";
import { useCSReportRunner } from "./useCSReportRunner";
import {
  downloadStudentProficiencyBucketsCSV,
  fetchStudentProficiencyBuckets,
} from "./csReportsApi";
import {
  buildReportParams,
  explicitColumns,
  validateBaseFilters,
} from "./reportHelpers";

const FLAT_COLUMNS = [
  ["session_date", "Session Date"],
  ["week_of", "Week of"],
  ["customer_name", "Customer"],
  ["school_name", "School"],
  ["teacher_name", "Teacher"],
  ["teacher_email", "Teacher Email"],
  ["student_name", "Student"],
  ["combined_student_id", "Student ID"],
  ["student_id_from_uch", "Student ID (UCH)"],
  ["student_id_from_json", "Student ID (JSON)"],
  ["student_math_proficiency", "Proficiency"],
  ["duration_greater_than_5_min", "Duration > 5 min"],
  ["session_id", "Session ID"],
  ["unique_id", "Unique ID"],
];

const ExtraField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ExtraLabel = styled.label`
  color: ${colors.nightlyWoods700 || "#374151"};
  font-size: 12px;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
`;

const PIVOT_COLUMNS = [
  ["week_of", "Week of"],
  ["level_1", "Proficiency 1"],
  ["level_2", "Proficiency 2"],
  ["level_3", "Proficiency 3"],
  ["unclassified", "Unclassified"],
  ["total_students", "Total Students"],
];

// Pick the bucket for a student-week from their classified proficiencies.
// Null rows (e.g. sub-5-min sessions where the model didn't classify) must be
// filtered out by the caller — they aren't "Level 0", they're "no signal" and
// shouldn't override valid ratings from other sessions that week.
// Tiebreaker: lower proficiency wins (conservative bias).
const pickBucketLevel = (classifiedProficiencies) => {
  if (classifiedProficiencies.length === 0) return null;
  const counts = { 1: 0, 2: 0, 3: 0 };
  classifiedProficiencies.forEach((p) => {
    if (p === 1 || p === 2 || p === 3) counts[p] += 1;
  });
  let bestLevel = null;
  let bestCount = -1;
  for (const level of [1, 2, 3]) {
    if (counts[level] > bestCount) {
      bestCount = counts[level];
      bestLevel = level;
    }
  }
  return bestLevel;
};

const buildPivotRows = (rows) => {
  if (!rows || rows.length === 0) return [];

  // Collect each student's classified proficiencies per week. A student is
  // "Unclassified" for the week only if they have zero classified sessions.
  const profsByStudentWeek = new Map();
  const studentsByWeek = new Map();
  rows.forEach((row) => {
    const week = row.week_of || "";
    const studentKey =
      row.combined_student_id || row.student_name || row.unique_id;
    if (!week || !studentKey) return;
    if (!studentsByWeek.has(week)) studentsByWeek.set(week, new Set());
    studentsByWeek.get(week).add(studentKey);
    const level = row.student_math_proficiency;
    if (level !== 1 && level !== 2 && level !== 3) return;
    const key = `${week}|${studentKey}`;
    if (!profsByStudentWeek.has(key)) profsByStudentWeek.set(key, []);
    profsByStudentWeek.get(key).push(level);
  });

  const byWeek = new Map();
  studentsByWeek.forEach((students, week) => {
    const buckets = {
      level_1: 0,
      level_2: 0,
      level_3: 0,
      unclassified: 0,
      total_students: 0,
    };
    students.forEach((studentKey) => {
      buckets.total_students += 1;
      const profs = profsByStudentWeek.get(`${week}|${studentKey}`) || [];
      const level = pickBucketLevel(profs);
      if (level === 1) buckets.level_1 += 1;
      else if (level === 2) buckets.level_2 += 1;
      else if (level === 3) buckets.level_3 += 1;
      else buckets.unclassified += 1;
    });
    byWeek.set(week, buckets);
  });

  return Array.from(byWeek.entries())
    .map(([week, buckets]) => ({ week_of: week, ...buckets }))
    .sort((a, b) => (a.week_of < b.week_of ? 1 : a.week_of > b.week_of ? -1 : 0));
};

export const StudentProficiencyBucketsTab = ({
  filters,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}) => {
  const [view, setView] = useState("pivot");
  const runner = useCSReportRunner({
    fetchJson: fetchStudentProficiencyBuckets,
    fetchCsv: downloadStudentProficiencyBucketsCSV,
    errorTag: "student-proficiency-buckets",
  });

  const validation = useMemo(
    () => validateBaseFilters({ filters, startDate, endDate }),
    [filters, startDate, endDate]
  );

  const params = useMemo(() => {
    if (!validation.ok) return null;
    return buildReportParams({ filters, startDate, endDate });
  }, [filters, startDate, endDate, validation.ok]);

  const pivotRows = useMemo(() => buildPivotRows(runner.rows), [runner.rows]);

  const flatColumns = useMemo(
    () => explicitColumns(FLAT_COLUMNS, runner.rows),
    [runner.rows]
  );

  const pivotColumns = useMemo(
    () => explicitColumns(PIVOT_COLUMNS, pivotRows),
    [pivotRows]
  );

  const handleViewChange = useCallback((next) => {
    setView(next);
  }, []);

  const extraControls = (
    <ExtraField>
      <ExtraLabel>View</ExtraLabel>
      <Form.Select
        value={view}
        onChange={(e) => handleViewChange(e.target.value)}
      >
        <option value="pivot">Pivot (Week × Proficiency)</option>
        <option value="flat">Flat (one row per student × session)</option>
      </Form.Select>
    </ExtraField>
  );

  return (
    <div>
      <FiltersBar
        filters={filters}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        extraControls={extraControls}
      />

      <ReportToolbar
        validation={validation}
        loading={runner.loading}
        downloading={runner.downloading}
        onRun={() => runner.run(params)}
        onDownload={() => runner.download(params)}
      />

      {runner.error && <Alert variant="danger">{String(runner.error)}</Alert>}

      {runner.rows && runner.rows.length > 0 && (
        <ProficiencyBucketChart rows={runner.rows} />
      )}

      {runner.rows !== null && (
        view === "pivot" ? (
          <DataTable
            data={pivotRows}
            columns={pivotColumns}
            emptyMessage="No CONSENSUS sessions in this date range for the selected filters."
          />
        ) : (
          <DataTable
            data={runner.rows}
            columns={flatColumns}
            emptyMessage="No CONSENSUS sessions in this date range for the selected filters."
          />
        )
      )}
    </div>
  );
};
